/**
 * Global "Session expired" modal.
 *
 * Mounted once, high in the tree (just under AuthProvider, inside the theme
 * provider). It is driven entirely by the framework-agnostic session-expiry
 * controller, so ALL three trigger paths surface the SAME modal:
 *   1. Proactive idle expiry (oidc `accessTokenExpired` / failed silent renew)
 *   2. Tab focus / visibility re-check after the laptop wakes
 *   3. An unrecoverable 401 from any axios layer or TanStack Query
 *
 * The boolean guard in the controller debounces a burst of parallel 401s into
 * a single modal. The primary action redirects to OpenIddict for re-login,
 * preserving the current URL so the user lands back where they were.
 */
import { useEffect, useState, useCallback } from 'react';
import { useTheme } from '@/theme';
import { useTranslation } from '@/i18n';
import { userManager } from './userManager';
import { subscribeSessionExpiry, isSessionExpired } from './sessionExpiry';

const ClockIcon = ({ size = 28, color }: { size?: number; color: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

export const SessionExpiredModal = () => {
  const { colors, typography, spacing, borderRadius, theme } = useTheme();
  const { t } = useTranslation();
  const [open, setOpen] = useState(() => isSessionExpired());
  const [isRedirecting, setIsRedirecting] = useState(false);

  // Subscribe to the controller — works regardless of which path fired.
  useEffect(() => subscribeSessionExpiry(setOpen), []);

  // Lock body scroll while the modal blocks the app.
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  const handleRelogin = useCallback(async () => {
    setIsRedirecting(true);
    try {
      // Preserve the current location so the post-login callback can return
      // the user to exactly where they were.
      await userManager.signinRedirect({ state: { returnUrl: window.location.href } });
    } catch (err) {
      console.error('[Auth] Failed to start re-login redirect:', err);
      setIsRedirecting(false);
    }
  }, []);

  if (!open) return null;

  const loginLabel = t('SessionExpired:LoginButton', { defaultValue: 'Log back in' });
  const redirectingLabel = t('SessionExpired:Redirecting', { defaultValue: 'Redirecting…' });

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: theme.colors.overlayBackdrop,
        // Sit above every other modal/dialog (modal token is 1100) but below toasts.
        zIndex: 9000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: spacing.lg,
      }}
      role="presentation"
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="session-expired-title"
        aria-describedby="session-expired-message"
        style={{
          backgroundColor: colors.backgroundCard,
          borderRadius: borderRadius.xl,
          boxShadow: theme.shadows.modal,
          maxWidth: '420px',
          width: '100%',
          padding: spacing.xl,
          animation: 'scaleIn 0.2s ease-out',
        }}
      >
        <style>
          {`@keyframes scaleIn { from { transform: scale(0.95); opacity: 0; } to { transform: scale(1); opacity: 1; } }`}
        </style>

        {/* Icon */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: spacing.lg }}>
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              backgroundColor: colors.warningLight,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ClockIcon size={28} color={colors.warning} />
          </div>
        </div>

        {/* Title */}
        <h2
          id="session-expired-title"
          style={{
            margin: 0,
            marginBottom: spacing.sm,
            fontSize: typography.fontSizeLg,
            fontWeight: typography.fontWeightSemibold,
            color: colors.textPrimary,
            textAlign: 'center',
          }}
        >
          {t('SessionExpired:Title', { defaultValue: 'Session expired' })}
        </h2>

        {/* Message */}
        <p
          id="session-expired-message"
          style={{
            margin: 0,
            marginBottom: spacing.xl,
            fontSize: typography.fontSizeBase,
            color: colors.textSecondary,
            textAlign: 'center',
            lineHeight: 1.5,
          }}
        >
          {t('SessionExpired:Message', {
            defaultValue:
              'Your session has expired for security reasons. Please log back in to continue where you left off.',
          })}
        </p>

        {/* Action */}
        <button
          type="button"
          onClick={handleRelogin}
          disabled={isRedirecting}
          autoFocus
          style={{
            width: '100%',
            padding: `${spacing.md} ${spacing.lg}`,
            backgroundColor: colors.brandAccent,
            border: 'none',
            borderRadius: borderRadius.md,
            fontSize: typography.fontSizeBase,
            fontWeight: typography.fontWeightMedium,
            color: 'white',
            cursor: isRedirecting ? 'not-allowed' : 'pointer',
            opacity: isRedirecting ? 0.7 : 1,
            transition: `all ${theme.transitions.default}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: spacing.sm,
          }}
        >
          {isRedirecting && (
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              style={{ animation: 'spin 1s linear infinite' }}
            >
              <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
              <path d="M21 12a9 9 0 1 1-6.219-8.56" />
            </svg>
          )}
          {isRedirecting ? redirectingLabel : loginLabel}
        </button>
      </div>
    </div>
  );
};

export default SessionExpiredModal;
